import { UnauthorizedHandler } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import { SESSION_EXPIRED_SIGN_IN_URL } from '../routing/session-expired';
import { getSharedFolderId, getSharedViewId, isPublicRegistrationRoute } from '../routing/utils';

type Dependencies = {
  /**
   *  Whether the app believes the user is signed in. A function rather than a value, because the
   *  handler is built once and the answer changes as the session does.
   */
  isAuthenticated: () => boolean;
  /**
   *  Decided once, when the app starts, rather than read here: an iframe cannot become a top-level
   *  window part-way through a session.
   */
  isEmbeddedInIframe: boolean;
  /**
   *  Clears the session locally. Deliberately not a Zitadel sign-out: the session has ended on its
   *  own, and there is nothing to tell Zitadel about.
   */
  logout: () => Promise<void>;
  /**
   *  Hands over an access token that has just been issued, for storing wherever the app reads the
   *  token from.
   */
  onAccessTokenRenewed: (accessToken: string) => void;
  /**
   *  Renews the session, answering with a freshly issued access token — or `undefined` when there is
   *  nothing to renew with and when the renewal failed, which the policy treats the same way.
   */
  renewAccessToken: () => Promise<string | undefined>;
};

/**
 *  What the client does when the backend refuses a request as unauthorized.
 *
 *  Three answers, in order:
 *
 *  1. **Nothing**, where a 401 is expected and a redirect would do harm — a tokenless request made
 *     while signed out (a failed sign-in, say), a shared view or folder, public registration, and inside an
 *     iframe, where navigating to `/signin` would expose the portal to whoever holds the
 *     access-token link.
 *  2. **Renew and retry**, when the session can be renewed. This is the ordinary case for a Zitadel
 *     session: the access token expires long before the refresh token does.
 *  3. **End the session**, which is what the client has always done — now saying why on the way out
 *     rather than dropping the user on a bare sign-in page.
 *
 *  A renewal is only attempted for the first refusal of a given request. The caller says so through
 *  `canRetryAfterRenew`, and that is what keeps a token the backend simply will not accept from
 *  turning into an endless renew-and-retry.
 *
 *  Built as a function of its dependencies — the renewal included — so the policy can be exercised
 *  on its own, without a rendered provider and the app-wide state that comes with it.
 */
export const createUnauthorizedHandler =
  ({
    isAuthenticated,
    isEmbeddedInIframe,
    logout,
    onAccessTokenRenewed,
    renewAccessToken,
  }: Dependencies): UnauthorizedHandler =>
  async ({ canRetryAfterRenew, requestCarriedToken }) => {
    if (isDefined(getSharedViewId()) || isDefined(getSharedFolderId())) {
      return false;
    }

    // A request that carried no token, from an app that does not think the user is signed in, was
    // not refused for a session that has ended. Its 401 is the answer to what the request itself
    // offered — a sign-in with the wrong password — and belongs to the caller, which shows it.
    // Redirecting would throw that answer away and blame a session that was never there. Decided by
    // the request and the session rather than by the page, so it holds wherever such a request is
    // made. A tokenless request from an app that *does* think so is a session that has gone — signed
    // out in another tab, say — and ends like any other.
    if (!requestCarriedToken && !isAuthenticated()) {
      return false;
    }

    if (isPublicRegistrationRoute()) {
      return false;
    }

    if (isEmbeddedInIframe) {
      return false;
    }

    if (canRetryAfterRenew) {
      const renewedToken = await renewAccessToken();

      if (isDefined(renewedToken)) {
        onAccessTokenRenewed(renewedToken);
        return true;
      }
    }

    await logout();
    window.location.href = SESSION_EXPIRED_SIGN_IN_URL;
    return false;
  };
